import React, { useEffect, useRef } from "react"

import { Textarea } from "@coval/ui"

import { InlineDiff } from "@/components/diff/InlineDiff"

import { usePendingEdit } from "../../../hooks/usePendingEdit"
import type { EditableTestCase } from "../../../model"
import { InputCellWrapper } from "./InputCellWrapper"

interface TextCellProps {
  testCase: EditableTestCase
  field: keyof EditableTestCase
  width: string
  isExpanded: boolean
  onUpdate: (updates: Partial<EditableTestCase>) => void
  placeholder?: string
  ariaLabel?: string
  /** Stable marker for locating this cell (forwarded to InputCellWrapper). */
  cellRole?: string
  isMarkedForDeletion?: boolean
  /** Override the edit mode render. Receives current value and change handler. */
  renderEdit?: (
    value: string,
    onChange: (value: string) => void,
    isExpanded: boolean,
  ) => React.ReactNode
  /** Override the read-only render. Receives current value. */
  renderReadOnly?: (
    value: string,
    isExpanded: boolean,
    isMarkedForDeletion: boolean,
  ) => React.ReactNode
}

/**
 * Generic text cell for the test case table.
 * Handles pending edit diffs, edit mode with cursor-at-end, and read-only display.
 * Pass renderEdit/renderReadOnly to customize for specialized fields (e.g. template variables, markdown).
 */
export const TextCell: React.FC<TextCellProps> = ({
  testCase,
  field,
  width,
  isExpanded,
  onUpdate,
  placeholder = "Enter text...",
  ariaLabel,
  cellRole,
  isMarkedForDeletion = false,
  renderEdit,
  renderReadOnly,
}) => {
  const { hasPendingEdit, oldValue, newValue } = usePendingEdit(testCase, field)
  const value = (testCase[field] as string) || ""

  const handleChange = (newVal: string) => {
    onUpdate({ [field]: newVal } as Partial<EditableTestCase>)
  }

  return (
    <InputCellWrapper
      width={width}
      isExpanded={isExpanded}
      hasPendingEdit={hasPendingEdit}
      isDisabled={isMarkedForDeletion}
      ariaLabel={ariaLabel || `Edit ${String(field)}`}
      cellRole={cellRole}
    >
      {({ isEditing, isExpanded: cellExpanded }) => {
        if (hasPendingEdit) {
          return (
            <div className="text-sm whitespace-pre-wrap break-words">
              <InlineDiff oldText={oldValue} newText={newValue!} />
            </div>
          )
        }

        if (isEditing) {
          if (renderEdit) {
            return renderEdit(value, handleChange, cellExpanded)
          }

          return (
            <AutoFocusTextarea
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
            />
          )
        }

        if (renderReadOnly) {
          return renderReadOnly(value, cellExpanded, isMarkedForDeletion)
        }

        if (value) {
          return (
            <div
              className={`text-sm text-coval-text whitespace-pre-wrap break-words ${
                isMarkedForDeletion ? "opacity-50 line-through" : ""
              }`}
            >
              {value}
            </div>
          )
        }

        return (
          <div className="text-sm text-coval-text-muted select-none leading-[20px]">
            {placeholder}
          </div>
        )
      }}
    </InputCellWrapper>
  )
}

/** Textarea that autofocuses with cursor at the end of the text. */
const AutoFocusTextarea: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder: string
}> = ({ value, onChange, placeholder }) => {
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (ref.current) {
      const el = ref.current
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  }, [])

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      placeholder={placeholder}
      aria-label={placeholder}
      className="bg-transparent shadow-none border-none p-0 min-h-0 text-sm text-coval-text resize-none focus:border-transparent"
    />
  )
}
